import { useEffect, useId, useRef, useState } from "react";
import type { FormEvent } from "react";
import { DialogShell } from "@/components/DialogShell";
import { StateMessage } from "@/components/StateMessage";
import { todayIso } from "@/components/AnimalStatusForm";
import { ApiError, problemMessage } from "@/api/client";
import type { AddAnimalWeightRequest, Animal, AnimalStatusChange, AnimalWeight, Guid } from "@/api/types";
import "./AnimalDetail.css";

function formatWhen(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

/** Newest `weighedOn` first (ISO dates sort lexically); the server already orders this way. */
function sortNewestFirst(rows: AnimalWeight[]): AnimalWeight[] {
  return [...rows].sort((a, b) => b.weighedOn.localeCompare(a.weighedOn));
}

/**
 * ef-041-5 — the animal's own detail view: its audit trail, `GET .../status-history`
 * (this slice's one API addition). Its own component/CSS pair, same convention every
 * other dialog in this app already follows (`ShiftDialog.tsx`, `AnimalDialog.tsx`) — kept
 * out of `Livestock.tsx`/`Livestock.css` so that file's own CSS budget
 * (`deploy/lib/css-budget.sh`, ADR 0009 §5, 250 lines) stays about the ROSTER, not a
 * second dialog's shell.
 *
 * Fetched LAZILY, on open — never held in `useLivestock.ts`'s own state (that hook's own
 * remarks): a farmer viewing one animal's history never pays for fetching everyone
 * else's.
 *
 * Deliberately does NOT also print "Current status: …" — the row/badge behind this
 * dialog already carries that, and the transition list's own `{fromStatus} → {toStatus}`
 * line is the ONLY place a status word appears in this view, so `livestock.spec.ts`'s own
 * `getByText(/active/i)`-style assertions resolve to exactly one element each rather than
 * colliding with a second, redundant one.
 *
 * ef-059-7 — also the animal's dated weighings: a newest-first list with an add form (a
 * second weighing on one day is refused by the server and shown here), and each history row
 * carries its EFFECTIVE date (the day the change took effect, which can differ from the day
 * it was recorded). Weights are fetched lazily here too, and `addWeight` reloads the roster
 * behind the dialog so the row's current weight follows the latest-dated weighing.
 */
export function AnimalDetail({
  animal,
  getHistory,
  getWeights,
  addWeight,
  onClose,
}: {
  animal: Animal;
  getHistory: (id: Guid) => Promise<AnimalStatusChange[]>;
  getWeights: (id: Guid) => Promise<AnimalWeight[]>;
  addWeight: (id: Guid, body: AddAnimalWeightRequest) => Promise<AnimalWeight>;
  onClose: () => void;
}) {
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [history, setHistory] = useState<AnimalStatusChange[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [weightsStatus, setWeightsStatus] = useState<"loading" | "ready" | "error">("loading");
  const [weights, setWeights] = useState<AnimalWeight[]>([]);
  const [weighedOn, setWeighedOn] = useState(todayIso);
  const [weightLb, setWeightLb] = useState("");
  const [weightError, setWeightError] = useState<string | null>(null);
  const [invalidField, setInvalidField] = useState<"date" | "weight" | null>(null);
  const [adding, setAdding] = useState(false);

  const titleId = useId();
  const dateId = useId();
  const weightId = useId();
  const weightErrorId = useId();
  const closeRef = useRef<HTMLButtonElement>(null);
  const weightInputRef = useRef<HTMLInputElement>(null);
  // Synchronous in-flight guard — a double click must not post two weighings.
  const addingRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getHistory(animal.id);
        if (cancelled) return;
        setHistory(rows);
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setError(problemMessage(err, "Could not load this animal's history."));
        setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [animal.id, getHistory]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const rows = await getWeights(animal.id);
        if (cancelled) return;
        setWeights(sortNewestFirst(rows));
        setWeightsStatus("ready");
      } catch {
        if (!cancelled) setWeightsStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [animal.id, getWeights]);

  async function submitWeight(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    if (addingRef.current) return;
    setWeightError(null);
    setInvalidField(null);
    const lbs = Number(weightLb);
    if (weightLb.trim() === "" || !Number.isFinite(lbs) || lbs <= 0) {
      setWeightError("Enter a weight greater than 0.");
      setInvalidField("weight");
      return;
    }
    const submitted = weightLb;
    addingRef.current = true;
    setAdding(true);
    try {
      await addWeight(animal.id, { weighedOn: weighedOn === "" ? undefined : weighedOn, weightLb: lbs });
      // Re-read rather than splice: the server owns the ordering and the one-per-day rule.
      setWeights(sortNewestFirst(await getWeights(animal.id)));
      setWeightsStatus("ready");
      // Clear only what was submitted: a value typed while the request was in flight is the
      // user's next entry, not ours to wipe (it would turn their resubmit into a blank form).
      setWeightLb((current) => (current === submitted ? "" : current));
      weightInputRef.current?.focus();
    } catch (err) {
      // A 409 (already weighed that day) and a 400 (future / before birth) both name the date.
      if (err instanceof ApiError && (err.status === 409 || err.status === 400)) setInvalidField("date");
      setWeightError(problemMessage(err, "Could not record this weight."));
    } finally {
      addingRef.current = false;
      setAdding(false);
    }
  }

  return (
    <DialogShell
      title={animal.tag}
      titleId={titleId}
      onClose={onClose}
      initialFocus={closeRef}
      footer={
        <button ref={closeRef} type="button" className="btn btn--ghost" onClick={onClose}>
          Close
        </button>
      }
    >
        <section className="animal-detail__section" aria-labelledby={`${titleId}-weights`} data-state={weightsStatus}>
          <h3 id={`${titleId}-weights`} className="animal-detail__subtitle">
            Weights
          </h3>
          <form className="animal-detail__weight-form" onSubmit={(e) => void submitWeight(e)} aria-busy={adding}>
            <span className="animal-detail__field">
              <label htmlFor={dateId}>Weigh date</label>
              <input
                id={dateId}
                type="date"
                max={todayIso()}
                value={weighedOn}
                aria-invalid={invalidField === "date" || undefined}
                aria-describedby={weightError ? weightErrorId : undefined}
                onChange={(e) => setWeighedOn(e.target.value)}
              />
            </span>
            <span className="animal-detail__field">
              <label htmlFor={weightId}>Weight (lbs)</label>
              <input
                id={weightId}
                ref={weightInputRef}
                type="number"
                inputMode="decimal"
                min="0"
                step="any"
                value={weightLb}
                aria-invalid={invalidField === "weight" || undefined}
                aria-describedby={weightError ? weightErrorId : undefined}
                onChange={(e) => setWeightLb(e.target.value)}
              />
            </span>
            <button type="submit" className="btn btn--primary" disabled={adding}>
              Add weight
            </button>
          </form>
          {weightError && (
            <p id={weightErrorId} className="animal-detail__error" role="alert">
              {weightError}
            </p>
          )}
          {weightsStatus === "loading" && <StateMessage tone="loading" title="Loading weights…" />}
          {weightsStatus === "error" && (
            <StateMessage tone="error" title="Could not load this animal's weights." />
          )}
          {weightsStatus === "ready" && (
            // @ui-check: always render the list once loaded — an empty one shows a single
            // "No weights recorded yet." item rather than collapsing to nothing.
            <ul className="animal-detail__weights" aria-label="Weights">
              {weights.length === 0 && <li className="animal-detail__empty">No weights recorded yet.</li>}
              {weights.map((w) => (
                <li key={w.id} className="animal-detail__weight-row" data-weighed-on={w.weighedOn}>
                  <span className="animal-detail__weight-value">{w.weightLb} lbs</span>
                  <span className="animal-detail__history-when">{w.weighedOn}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <h3 className="animal-detail__subtitle">History</h3>
        {status === "loading" && <StateMessage tone="loading" title="Loading history…" />}
        {status === "error" && (
          <StateMessage tone="error" title="Could not load this animal's history." detail={error ?? undefined} />
        )}
        {status === "ready" && history.length === 0 && (
          <p className="animal-detail__empty">No status changes recorded yet.</p>
        )}
        {status === "ready" && history.length > 0 && (
          <ul className="animal-detail__history-list">
            {history.map((row) => (
              <li key={row.id} className="animal-detail__history-row">
                <span className="animal-detail__history-transition">
                  {row.fromStatus} → {row.toStatus}
                </span>
                {row.reason && <span className="animal-detail__history-reason">{row.reason}</span>}
                <span className="animal-detail__history-when">Effective {row.effectiveOn}</span>
                <span className="animal-detail__history-when">Recorded {formatWhen(row.changedAt)}</span>
              </li>
            ))}
          </ul>
        )}
    </DialogShell>
  );
}
